// Instrument-level conflict detection for the instrument editors.
//
// The ⚠ conflict badge in InstrumentEditor is fed by these helpers. A conflict
// exists when:
//   (a) one of the instrument's tracks uses an I/O channel already taken by
//       ANOTHER person on the same rack (from the band-wide rack usage),
//   (b) two of the person's OWN tracks share the same I/O channel on the same
//       rack, or
//   (c) two of the person's tracks share the same input-list channel.
//
// The detection is PURE and deterministic (no i18n inside), so it is unit
// tested. The i18n message is formatted separately by formatConflictMessage.

import type { Track, StageRack, Instrument } from '../types';
import type { RackUsage } from './rackUsage';

export type InstrumentConflictKind = 'io-taken' | 'io-duplicate' | 'input-duplicate';

export interface InstrumentConflict {
  kind: InstrumentConflictKind;
  channel: number;
  rackName?: string;
  occupant?: string;
}

export interface DetectInstrumentConflictParams {
  /** Tracks of the instrument the badge is rendered on. */
  tracks: Track[];
  /** All tracks of the person (across every instrument) — for (b)/(c). */
  allPersonTracks: Track[];
  racks: StageRack[];
  /** Band-wide rack usage (optional — (a) is skipped when absent). */
  usage?: RackUsage;
  /** Person name, used to tell "my own track" from "someone else's". */
  personLabel: string;
  /** Name of the CURRENT instrument (e.g. "Trombone"). Used to decide whether
   * a channel taken by another person is a real conflict: two people who play
   * the SAME instrument (a main player and a replacement/guest) share the same
   * tracks because they never play at the same time — so it is NOT a conflict. */
  instrumentName?: string;
  /** Map of every OTHER person's label → the set of instrument names they play.
   * When a channel is taken by another person (io-taken), if that person plays
   * an instrument with the same name as `instrumentName`, the channel is shared
   * (replacement/guest) and is NOT flagged as a conflict. */
  personInstruments?: Record<string, Set<string>>;
}

/**
 * Build the `personInstruments` map (person label → set of instrument names)
 * from the band's full musician + crew lists. Pass it to
 * detectInstrumentConflict so a channel taken by someone who plays the SAME
 * instrument is treated as a shared track (replacement/guest), not a conflict.
 */
export function buildPersonInstruments(
  musicians: { name?: string; instruments?: Instrument[] }[],
  crew: { name?: string; instruments?: Instrument[] }[],
): Record<string, Set<string>> {
  const map: Record<string, Set<string>> = {};
  const add = (person: { name?: string; instruments?: Instrument[] }) => {
    const label = person.name || 'Unnamed';
    const set = map[label] ?? (map[label] = new Set<string>());
    for (const inst of person.instruments ?? []) {
      if (inst.name) set.add(inst.name);
    }
  };
  for (const m of musicians ?? []) add(m);
  for (const c of crew ?? []) add(c);
  return map;
}

/**
 * Detect the FIRST conflict on an instrument (checked in priority order:
 * own-channel duplicate → taken by another person → input-list duplicate).
 * Returns null when the instrument has no conflict.
 */
export function detectInstrumentConflict(p: DetectInstrumentConflictParams): InstrumentConflict | null {
  const rackName = (id?: string) => p.racks.find((r) => r.id === id)?.name || id || '';
  const isOwn = (label: string) => !!p.personLabel && label.startsWith(p.personLabel);

  // Does the occupant of a channel play the SAME instrument as the current one?
  // If so, the two people share the tracks (replacement/guest) → not a conflict.
  // ONLY when the track itself DECLARES the sharing (`sharedWithReplacement`):
  // otherwise a channel patched by anyone else is a real conflict. (2026-09-20)
  const occupantSharesInstrument = (occ: string, shared?: boolean): boolean => {
    if (!shared) return false;
    if (!p.instrumentName || !p.personInstruments) return false;
    for (const [label, insts] of Object.entries(p.personInstruments)) {
      if (occ.startsWith(label)) return insts.has(p.instrumentName);
    }
    return false;
  };

  // (b) two of the person's OWN tracks share an I/O channel on the same rack.
  const ioCount = new Map<string, number>();
  for (const tr of p.allPersonTracks) {
    if (tr.rackId && tr.ioChannel) {
      const key = `${tr.rackId}|${tr.ioChannel}`;
      ioCount.set(key, (ioCount.get(key) || 0) + 1);
    }
  }
  for (const tr of p.tracks) {
    if (!tr.rackId || !tr.ioChannel) continue;
    if ((ioCount.get(`${tr.rackId}|${tr.ioChannel}`) || 0) > 1) {
      return { kind: 'io-duplicate', channel: tr.ioChannel, rackName: rackName(tr.rackId) };
    }
  }

  // (a) an I/O channel already taken by ANOTHER person on the same rack.
  if (p.usage) {
    for (const tr of p.tracks) {
      if (!tr.rackId || !tr.ioChannel) continue;
      const occ = p.usage.inputs[tr.rackId]?.[tr.ioChannel];
      if (occ && !isOwn(occ) && !occupantSharesInstrument(occ, tr.sharedWithReplacement)) {
        return { kind: 'io-taken', channel: tr.ioChannel, rackName: rackName(tr.rackId), occupant: occ };
      }
    }
  }

  // (c) two of the person's tracks share the same input-list channel.
  const inCount = new Map<number, number>();
  for (const tr of p.allPersonTracks) {
    if (tr.inputListChannel) inCount.set(tr.inputListChannel, (inCount.get(tr.inputListChannel) || 0) + 1);
  }
  for (const tr of p.allPersonTracks) {
    if (tr.inputListChannel && (inCount.get(tr.inputListChannel) || 0) > 1) {
      return { kind: 'input-duplicate', channel: tr.inputListChannel };
    }
  }

  return null;
}

/** Format a detected conflict as a short human message via i18n. */
export function formatConflictMessage(
  t: (key: string, opts?: Record<string, unknown>) => string,
  c: InstrumentConflict,
): string {
  switch (c.kind) {
    case 'io-taken':
      return t('instrumentConflict.ioTaken', { ch: c.channel, rack: c.rackName || '—', who: c.occupant || '' });
    case 'io-duplicate':
      return t('instrumentConflict.ioDuplicate', { ch: c.channel, rack: c.rackName || '—' });
    case 'input-duplicate':
      return t('instrumentConflict.inputDuplicate', { ch: c.channel });
  }
}
